// Custom branding — the company's logo and primary app colour.
//
// The colour is the MOBILE APP's brand, not the console's: it is deliberately
// never wired to --brand, so picking a colour here cannot repaint the admin UI.
//
// The logo uploads on SAVE, not on pick: the picked File sits in the draft and
// save() reserves → PUTs → POSTs the returned mediaId, so abandoning the edit
// uploads nothing. The POST takes the full section, so a colour-only change
// still echoes the current media id back or the server reads it as a clear.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import SettingsCard, { SettingsRow } from './SettingsCard';
import useCompanyMediaUrl from './useCompanyMediaUrl';
import { Button, Field, Icon, Input } from '@/components/common';
import {
  nullIfEmpty,
  useUpdateBrandDetailsMutation,
  type BrandDetails,
} from '@/api/endpoints/companySettingsApi';
import { useBrandDetailsMediaMutation } from '@/api/endpoints/mediaApi';
import { uploadToPresignedUrl } from '@/utils/uploadFile';
import type { ApiErrorShape } from '@/api/types';

interface CustomBrandingCardProps {
  brand: BrandDetails;
}

/** Leading swatch is the Midani brand green; the rest reuse the palette the
 *  form builder's submit-button picker already ships, so the two colour rows in
 *  the app offer the same family. */
const PRESET_COLORS = ['#0E8F6E', '#3b82f6', '#7c6cf0', '#e0a44a', '#d65563', '#1e2430'];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// The constraints the field's own hint promises. A spec shown but not enforced
// is decoration, so all three are checked on pick.
const ACCEPT = 'image/png,image/svg+xml';
const MAX_BYTES = 2 * 1024 * 1024;
const MIN_EDGE = 512;

/** SVG has no intrinsic raster size, so the dimension floor doesn't apply. */
const dimensionsOk = (file: File): Promise<boolean> =>
  file.type === 'image/svg+xml'
    ? Promise.resolve(true)
    : new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(img.naturalWidth >= MIN_EDGE && img.naturalHeight >= MIN_EDGE);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(false);
        };
        img.src = url;
      });

const CustomBrandingCard = ({ brand }: CustomBrandingCardProps) => {
  const { t } = useTranslation();
  const [updateBrand, { isLoading: saving }] = useUpdateBrandDetailsMutation();
  const [reserveMedia] = useBrandDetailsMediaMutation();
  const fileRef = useRef<HTMLInputElement>(null);

  const seed = useMemo<BrandDetails>(() => ({ ...brand }), [brand]);
  const [draft, setDraft] = useState<BrandDetails>(seed);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setDraft(seed);
    setPendingFile(null);
  }, [seed]);

  // Read mode shows what is stored; edit mode shows the pending pick if there is one.
  const readUrl = useCompanyMediaUrl(seed.media);
  const editUrl = useCompanyMediaUrl(draft.media, pendingFile);

  const hexInvalid = !HEX_RE.test(draft.brandColorScheme);
  const dirty =
    draft.brandColorScheme !== seed.brandColorScheme ||
    draft.media !== seed.media ||
    pendingFile !== null;
  const canSave = dirty && !hexInvalid;

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPT.split(',').includes(file.type)) {
      toast.error(t('company_settings_logo_type_error'));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t('company_settings_logo_size_error'));
      return;
    }
    if (!(await dimensionsOk(file))) {
      toast.error(t('company_settings_logo_dimensions_error'));
      return;
    }
    setPendingFile(file);
  };

  const cancel = () => {
    setDraft(seed);
    setPendingFile(null);
    setEditing(false);
  };

  const save = async () => {
    let media = draft.media;
    try {
      if (pendingFile) {
        setUploading(true);
        const ext = pendingFile.name.split('.').pop() || 'png';
        const { uploadUrl, mediaId } = await reserveMedia({
          mediaType: 'image',
          imageName: `${Date.now()}.${ext}`,
        }).unwrap();
        await uploadToPresignedUrl(uploadUrl, pendingFile);
        media = mediaId;
      }
    } catch (err) {
      // The upload failed, so nothing is saved and the draft is kept — the user
      // can retry without re-entering the colour.
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
      return;
    } finally {
      setUploading(false);
    }

    try {
      await updateBrand({
        media: nullIfEmpty(media),
        brandColorScheme: nullIfEmpty(draft.brandColorScheme),
      }).unwrap();
      setEditing(false);
      setPendingFile(null);
      toast.success(t('company_settings_branding_saved'));
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <SettingsCard
      title={t('company_settings_branding_title')}
      subtitle={t('company_settings_branding_subtitle')}
      editing={editing}
      dirty={dirty}
      saving={saving || uploading}
      canSave={canSave}
      onEdit={() => setEditing(true)}
      onCancel={cancel}
      onSave={() => void save()}
    >
      {editing ? (
        <div className='company-settings__form'>
          <div className='company-settings__row'>
            <p className='company-settings__label'>
              {t('company_settings_logo')}
              <span className='company-settings__label-spec'>
                {t('company_settings_logo_spec')}
              </span>
            </p>
            <div className='company-settings__logo-edit'>
              <span className='company-settings__logo-tile company-settings__logo-tile--edit'>
                {editUrl ? (
                  <img src={editUrl} alt='' className='company-settings__logo-img' />
                ) : (
                  <Icon name='image' size={22} />
                )}
              </span>
              <span className='company-settings__logo-meta'>
                {/* Only a pending pick has a real filename — a stored logo was
                    renamed on upload, so there is nothing truthful to show. */}
                {pendingFile && (
                  <span className='company-settings__logo-name'>{pendingFile.name}</span>
                )}
                <span className='company-settings__logo-actions'>
                  <Button
                    variant='secondary'
                    size='sm'
                    iconStart='upload'
                    onClick={() => fileRef.current?.click()}
                  >
                    {t('company_settings_logo_replace')}
                  </Button>
                  {(draft.media || pendingFile) && (
                    <Button
                      variant='secondary'
                      size='sm'
                      iconStart='trash-2'
                      onClick={() => {
                        setPendingFile(null);
                        setDraft((d) => ({ ...d, media: '' }));
                      }}
                    >
                      {t('company_settings_logo_remove')}
                    </Button>
                  )}
                </span>
              </span>
            </div>
            <p className='company-settings__hint'>{t('company_settings_logo_hint')}</p>
            <input
              ref={fileRef}
              type='file'
              accept={ACCEPT}
              className='company-settings__file'
              onChange={(e) => {
                void pickFile(e.target.files?.[0]);
                // Reset so re-picking the same file still fires a change.
                e.target.value = '';
              }}
            />
          </div>

          <Field
            label={t('company_settings_brand_color')}
            error={hexInvalid ? t('company_settings_hex_invalid') : undefined}
          >
            <div className='company-settings__colors'>
              {PRESET_COLORS.map((color) => {
                const active = draft.brandColorScheme.toLowerCase() === color.toLowerCase();
                return (
                  <button
                    key={color}
                    type='button'
                    aria-label={color}
                    aria-pressed={active}
                    className={`company-settings__swatch${active ? ' is-selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setDraft((d) => ({ ...d, brandColorScheme: color }))}
                  >
                    {active && <Icon name='check' size={16} />}
                  </button>
                );
              })}
              <Input
                dir='ltr'
                value={draft.brandColorScheme}
                error={hexInvalid}
                maxLength={7}
                className='company-settings__hex'
                aria-label={t('company_settings_brand_color')}
                onChange={(e) => setDraft((d) => ({ ...d, brandColorScheme: e.target.value }))}
              />
            </div>
          </Field>
        </div>
      ) : (
        <div className='company-settings__rows'>
          <div className='company-settings__row'>
            <p className='company-settings__label'>{t('company_settings_logo')}</p>
            <span className='company-settings__logo-tile'>
              {readUrl ? (
                <img src={readUrl} alt='' className='company-settings__logo-img' />
              ) : (
                <Icon name='image' size={22} />
              )}
            </span>
          </div>
          <SettingsRow
            label={t('company_settings_brand_color')}
            value={seed.brandColorScheme}
            swatch={seed.brandColorScheme}
            ltr
            mono
          />
        </div>
      )}
    </SettingsCard>
  );
};

export default CustomBrandingCard;
